import { useEffect, useMemo, useState } from "react";
import { api, money } from "../api";
import { useT } from "../i18n";
import { SC } from "../scenes";
import { Plate, useApi, useShell } from "../ui";
import { SendDialog } from "./Builder";

export interface PackageRow {
  slug: string; title: string; exclusive: boolean; interests: string[]; category?: string; country?: string; currency?: string;
  origins: { airport: string; nights: number[]; defaultNights: number }[]; stops: { airport: string; nights: number }[];
  content: { tagline?: string; summary?: string }; includedServices: string[];
  fromPrice?: { amountMinor: number; currency: string; nights: number; origin: string; departDate: string };
  price?: { retailMinor: number; netMinor?: number; clientMinor: number; earnMinor?: number; commissionMinor?: number; youOweMinor?: number };
}

const catOf = (p: PackageRow) => p.category ?? p.interests[0] ?? "Packages";
/** A CMS category key (`city_break`, `CITY_BREAK`) as people read it: the dictionary's label, else title-cased. */
export function catLabel(key: string, t: (k: string) => string): string {
  const norm = key.trim().toLowerCase().replace(/[\s-]+/g, "_");
  const known = t(`cat.${norm}`);
  if (known !== `cat.${norm}`) return known;
  const words = norm.split("_").filter(Boolean).join(" ");
  return words ? words[0]!.toUpperCase() + words.slice(1) : key;
}
/** "Who is it for": the prototype's reading of each collection. */
const WHO: Record<string, string> = { "City Break": "Couples and friends, 4–6 nights", "Road Trip": "Independent travellers who drive", "Cruises": "First cruisers and families", "Christmas Markets": "Couples and groups, Nov–Dec", "All-Inclusive": "Families and beach lovers", "Train Trips": "Slow travellers, no car", "Island & City": "Two moods in one trip", "Food & Wine": "Foodies and couples", "Premium": "Clients who want the best rooms" };

export function PackageCard({ p }: { p: PackageRow }) {
  const t = useT();
  const [quoting, setQuoting] = useState(false);
  const nights = p.fromPrice?.nights ?? p.origins[0]?.defaultNights ?? 0;
  const perPerson = p.price ? Math.round(p.price.clientMinor / 2) : undefined;
  return (
    <article className="pk" tabIndex={0}>
      <Plate svg={SC.forCat(catOf(p))}>{p.exclusive ? <span className="pk-more">{t("pk.expertOnly")}</span> : null}</Plate>
      <div className="b">
        <span className="cat">{catLabel(catOf(p), t)}{p.country ? ` · ${p.country}` : ""}{p.exclusive ? <> · <span className="tag-ex">{t("pk.expertOnly")}</span></> : null}</span>
        <h4>{p.title}</h4>
        <p className="loc">{p.content.tagline ?? p.stops.map((s) => s.airport).join(" · ")}</p>
        <div className="foot">
          <div className="price"><small>{t("pk.from", { n: nights })}</small><b>{money(perPerson, p.currency)}</b> <span>{t("pk.person")}</span>{p.price?.earnMinor !== undefined ? <small className="mg"> · you earn {money(Math.round(p.price.earnMinor / 2), p.currency)}</small> : p.price?.commissionMinor !== undefined ? <small className="mg"> · commission {money(Math.round(p.price.commissionMinor / 2), p.currency)}</small> : null}</div>
          <div className="actions"><button className="btn ink" type="button" onClick={() => setQuoting(true)}>{t("pk.quote")}</button></div>
        </div>
      </div>
      {quoting ? <PackageQuote p={p} onClose={() => setQuoting(false)} /> : null}
    </article>
  );
}

/** "Quote" on a package (docs/37 §5.2): travellers, the margin inside the cap, and send to a client. */
function PackageQuote({ p, onClose }: { p: PackageRow; onClose: () => void }) {
  const t = useT();
  const { me, toast } = useShell();
  const [pax, setPax] = useState(2);
  const cap = me.tier?.cap ?? 10;
  const [margin, setMargin] = useState(Math.min(10, cap));
  const [sending, setSending] = useState<string | null>(null);
  const retail = Math.round(((p.fromPrice?.amountMinor ?? 0) / 2) * pax);
  const net = Math.round(retail / 1.1);
  const client = me.expert ? Math.round(net * (1 + margin / 100)) : retail;
  const save = async () => {
    const { quote } = await api<{ quote: { id: string } }>("/quotes", { body: { title: p.title, currency: p.currency ?? "USD", marginPct: me.expert ? margin : 10, lines: [{ kind: "package", title: p.title, retailMinor: retail, ref: { type: "package", slug: p.slug } }], travellers: { adults: pax }, nights: p.fromPrice?.nights, destination: p.title, departDate: p.fromPrice?.departDate, origin: p.fromPrice?.origin } });
    return quote.id;
  };
  return (
    <div className="overlay" onClick={(e) => e.target === e.currentTarget && onClose()}><div className="dialog" role="dialog" aria-modal="true" aria-label="Quote">
      <div className="dlg-top"><div className="row"><b>{p.title}</b><button className="x" type="button" onClick={onClose} aria-label="Close">×</button></div></div>
      <div className="quote">
        <div className="q-head"><span className="eyebrow">Quote</span><b>{p.title}</b><span className="muted">{p.fromPrice?.nights} nights · from {p.fromPrice?.origin}</span></div>
        <div className="q-row"><label>Travellers</label><div className="stepper"><button type="button" aria-label="Fewer travellers" onClick={() => setPax(Math.max(1, pax - 1))}>−</button><output>{pax}</output><button type="button" aria-label="More travellers" onClick={() => setPax(Math.min(9, pax + 1))}>+</button></div></div>
        {me.expert ? (
          <div className="q-row col mg-hide">
            <div className="q-lbl"><label htmlFor="qt-m">{t("bk.margin")}</label><output>{margin}%</output></div>
            <input type="range" id="qt-m" min={0} max={cap} step={0.5} value={margin} onChange={(e) => setMargin(Number(e.target.value))} />
            <div className="q-scale"><span>{t("bk.best")}</span><span>{t("bk.recommended")}</span><span>{t("bk.higher")}</span></div>
          </div>
        ) : null}
        <dl className="q-sum">
          {me.expert ? <div className="mg"><dt>{t("bk.net")} <small>{t("bk.netSmall")}</small></dt><dd>{money(net, p.currency)}</dd></div> : null}
          <div className="tot"><dt>{t("bk.client")}</dt><dd>{money(client, p.currency)}</dd></div>
          {me.expert ? <div className="earn mg"><dt>{t("bk.earn")}</dt><dd>{money(client - net, p.currency)}</dd></div> : <div className="mg"><dt>Commission</dt><dd>{money(Math.round(retail * ((p.price?.commissionMinor ?? 0) / Math.max(1, p.price?.retailMinor ?? 1))), p.currency)}</dd></div>}
        </dl>
        <p className="q-note">{me.expert ? t("bk.cap", { tier: t(`tier.${me.partner.tier}`), cap }) : null} The Getaways margin is fixed on every package, whatever margin you choose.</p>
        <div className="q-actions"><button className="btn line" type="button" onClick={async () => { await save(); toast("Saved to Trips."); onClose(); }}>{t("bk.save")}</button><button className="btn ink" type="button" onClick={async () => setSending(await save())}>{t("bk.send")}</button></div>
      </div>
      {sending ? <SendDialog quoteId={sending} onClose={() => { setSending(null); onClose(); }} /> : null}
    </div></div>
  );
}

export function Packages() {
  const t = useT();
  const { data, error } = useApi<{ packages: PackageRow[] }>("/packages");
  const [cat, setCat] = useState("All");
  const [q, setQ] = useState(sessionStorage.getItem("station.pkq") ?? "");
  const [sort, setSort] = useState("price");
  const [limit, setLimit] = useState(24);
  useEffect(() => {
    const on = () => setQ(sessionStorage.getItem("station.pkq") ?? "");
    window.addEventListener("station:pkq", on);
    return () => { window.removeEventListener("station:pkq", on); sessionStorage.removeItem("station.pkq"); };
  }, []);
  const all = data?.packages ?? [];
  const cats = useMemo(() => { const c: Record<string, number> = {}; for (const p of all) c[catOf(p)] = (c[catOf(p)] ?? 0) + 1; return Object.entries(c).sort((a, b) => b[1] - a[1]); }, [all]);
  const list = all.filter((p) => (cat === "All" || (cat === "Expert only" ? p.exclusive : catOf(p) === cat)) && (!q || `${p.title} ${p.content.tagline ?? ""} ${catOf(p)} ${p.country ?? ""}`.toLowerCase().includes(q.toLowerCase())))
    .sort((a, b) => { const k = sort.replace("-", ""); const d = sort.startsWith("-") ? -1 : 1; const v = (p: PackageRow) => (k === "price" ? p.price?.clientMinor ?? 0 : p.fromPrice?.nights ?? 0); return (v(a) - v(b)) * d; });
  const exclusive = all.filter((p) => p.exclusive).length;
  return (
    <>
      <div className="pk-head">
        <div><span className="eyebrow">{t("pk.eyebrow")}</span><h2>{cat === "All" ? t("pk.all") : cat === "Expert only" ? t("pk.expertOnly") : catLabel(cat, t)} · {list.length}</h2></div>
        <div className="pk-tools">
          <label className="search"><input placeholder={t("pk.search")} aria-label="Search packages" value={q} onChange={(e) => { setQ(e.target.value); setLimit(24); }} /></label>
          <select aria-label="Sort" value={sort} onChange={(e) => setSort(e.target.value)}>{["price", "-price", "nights", "-nights"].map((s) => <option key={s} value={s}>{t(`pk.sort.${s}`)}</option>)}</select>
        </div>
      </div>
      <div className="chips cats" role="group" aria-label="Collections">
        <button className="chip" type="button" aria-pressed={cat === "All"} onClick={() => setCat("All")}>All <small>{all.length}</small></button>
        {exclusive ? <button className="chip" type="button" aria-pressed={cat === "Expert only"} onClick={() => setCat("Expert only")}>{t("pk.expertOnly")} <small>{exclusive}</small></button> : null}
        {cats.map(([c, n]) => <button key={c} className="chip" type="button" aria-pressed={cat === c} onClick={() => { setCat(c); setLimit(24); }}>{catLabel(c, t)} <small>{n}</small></button>)}
      </div>
      {cat !== "All" && WHO[cat] ? <p className="muted" style={{ marginBottom: "1rem" }}><b>{t("pk.who")}:</b> {WHO[cat]}</p> : null}
      {error ? <div className="empty">{error}</div> : null}
      <div className="grid">{list.length ? list.slice(0, limit).map((p) => <PackageCard key={p.slug} p={p} />) : data ? <div className="empty">{t("pk.none")}</div> : null}</div>
      {list.length > limit ? <button className="btn line more" type="button" onClick={() => setLimit(limit + 24)}>{t("pk.more")}</button> : null}
      <p className="src">Prices per person from the US departure, for two travellers; the client price is at the recommended 10% margin. Prices change daily.</p>
    </>
  );
}
